import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Save, Shield, Upload } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

export default function Settings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  // Form state for company info
  const [companyName, setCompanyName] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [abn, setAbn] = useState("");
  const [introText, setIntroText] = useState("");
  const [termsText, setTermsText] = useState("");

  // Fetch current settings
  const { data: settings, isLoading } = useQuery({
    queryKey: ["app-settings"],
    queryFn: async () => {
      const response = await fetch(`${apiUrl}/api/v1/settings`);
      if (!response.ok) {
        throw new Error("Failed to fetch settings");
      }
      const data = await response.json();

      // Initialize form state
      if (data.data) {
        setCompanyName(data.data.company_name || "");
        setAddressLine1(data.data.company_address_line1 || "");
        setAddressLine2(data.data.company_address_line2 || "");
        setPhone(data.data.company_phone || "");
        setWebsite(data.data.company_website || "");
        setAbn(data.data.company_abn || "");
        setIntroText(data.data.quote_intro_text || "");
        setTermsText(data.data.quote_terms_text || "");
      }

      return data.data;
    },
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`${apiUrl}/api/v1/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        throw new Error("Failed to update settings");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-settings"] });
      toast({
        title: "Settings Updated",
        description: "Your settings have been saved successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update settings",
        variant: "destructive",
      });
    },
  });

  // Upload logo mutation
  const uploadLogoMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("logo", file);

      const response = await fetch(`${apiUrl}/api/v1/settings/upload-logo`, {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Failed to upload logo");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["app-settings"] });
      setLogoFile(null);
      setLogoPreview(null);
      toast({
        title: "Logo Uploaded",
        description: "Your logo has been uploaded successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to upload logo",
        variant: "destructive",
      });
    },
  });

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = () => {
    if (logoFile) {
      uploadLogoMutation.mutate(logoFile);
    }
  };

  const handleSaveAllSettings = () => {
    updateSettingsMutation.mutate({
      company_name: companyName,
      company_address_line1: addressLine1,
      company_address_line2: addressLine2,
      company_phone: phone,
      company_website: website,
      company_abn: abn,
      quote_intro_text: introText,
      quote_terms_text: termsText,
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          onNewProject={() => navigate("/new-project")}
          onViewProjects={() => navigate("/projects")}
        />
        <main className="container mx-auto p-6">
          <div className="text-center">Loading settings...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        onNewProject={() => navigate("/new-project")}
        onViewProjects={() => navigate("/projects")}
      />

      <main className="container mx-auto p-6 max-w-4xl">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Customize your quote appearance and company information
          </p>
        </div>

        <div className="space-y-6">
          {/* User Management (Admin Only) */}
          {user?.role === "admin" && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  User Management
                </CardTitle>
                <CardDescription>Manage system users and their permissions</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Create new users, modify roles, and manage access to the quoting system.
                </p>
                <Button onClick={() => navigate("/user-management")}>
                  <Shield className="w-4 h-4 mr-2" />
                  Go to User Management
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Company Information
              </CardTitle>
              <CardDescription>This information appears on all your quotes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="company-name">Company Name</Label>
                  <Input
                    id="company-name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="RENO WARRIORS PTY LTD"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address-line-1">Address Line 1</Label>
                  <Input
                    id="address-line-1"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    placeholder="Po Box 25"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="address-line-2">Address Line 2</Label>
                  <Input
                    id="address-line-2"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    placeholder="Patterson Lakes  Vic 3197"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Ph: 1300 788 815"
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder="www.renowarriors.com.au"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="abn">ABN / Business Number</Label>
                  <Input
                    id="abn"
                    value={abn}
                    onChange={(e) => setAbn(e.target.value)}
                    placeholder="ABN: 70 618 035 377"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Logo Upload */}
          <Card>
            <CardHeader>
              <CardTitle>Company Logo</CardTitle>
              <CardDescription>
                Upload your company logo to appear on the top right of quotes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {(logoPreview || settings?.logo_url) && (
                <div className="flex items-center gap-4">
                  <div className="w-48 h-32 border rounded-lg flex items-center justify-center bg-muted p-2">
                    <img
                      src={logoPreview || `${apiUrl}${settings?.logo_url}`}
                      alt="Logo preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">Current logo preview</p>
                </div>
              )}
              <div className="flex items-center gap-4">
                <Input
                  id="logo-upload"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoChange}
                  className="max-w-xs"
                />
                <Button
                  onClick={handleUploadLogo}
                  disabled={!logoFile || uploadLogoMutation.isPending}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadLogoMutation.isPending ? "Uploading..." : "Upload Logo"}
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                Accepted formats: PNG, JPEG, SVG. Recommended size: 400x200px
              </p>
            </CardContent>
          </Card>

          {/* Quote Introduction Text */}
          <Card>
            <CardHeader>
              <CardTitle>Quote Introduction Text</CardTitle>
              <CardDescription>
                This text appears at the top of every quote, below the header
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={introText}
                onChange={(e) => setIntroText(e.target.value)}
                rows={4}
                placeholder="We thank you for allowing us the opportunity to quote on your project..."
                className="resize-none"
              />
            </CardContent>
          </Card>

          {/* Quote Terms Text */}
          <Card>
            <CardHeader>
              <CardTitle>Quote Terms & Conditions</CardTitle>
              <CardDescription>
                Legal terms and conditions appearing below the introduction
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Textarea
                value={termsText}
                onChange={(e) => setTermsText(e.target.value)}
                rows={6}
                placeholder="This QUOTATION is subject to the terms and conditions specified below..."
                className="resize-none"
              />
              <p className="text-sm text-muted-foreground mt-2">
                Use {"{quote_number}"} and {"{quote_date}"} as placeholders
              </p>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => navigate(-1)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAllSettings} disabled={updateSettingsMutation.isPending}>
              <Save className="w-4 h-4 mr-2" />
              {updateSettingsMutation.isPending ? "Saving..." : "Save All Settings"}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
